import { useState, useEffect } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

// Assign a single machine to a customer (from the machine's menu on the dashboard).
export function AssignCustomerDialog({ open, onOpenChange, machine, onAssigned }) {
  const { currentOrgId } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !currentOrgId) return;
    setCustomerId(machine?.customer_id || "");
    api.get(`/support/customers?org_id=${currentOrgId}&status=active`).then((r) => setCustomers(r.data)).catch(() => {});
  }, [open, currentOrgId, machine]);

  if (!machine) return null;

  const save = async (unassign = false) => {
    setSaving(true);
    try {
      await api.patch(`/support/machines/${machine.machine_id}/customer`, {
        org_id: currentOrgId, customer_id: unassign ? null : (customerId || null),
      });
      toast.success(unassign ? "Computer unassigned" : "Computer assigned to customer");
      onOpenChange(false);
      onAssigned && onAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update assignment");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10" data-testid="assign-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Assign to customer</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Link <span className="text-zinc-200">{machine.name}</span> to a customer so future tickets, time and remote sessions roll up to them.
            {machine.customer_name && <> Currently assigned to <span className="text-zinc-200">{machine.customer_name}</span>.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="py-1">
          <Label className="text-xs text-zinc-400">Customer</Label>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger data-testid="assign-customer-select" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="Select a customer" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              {customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2">
          {machine.customer_id && (
            <Button variant="ghost" data-testid="assign-customer-unassign" onClick={() => save(true)} disabled={saving} className="text-zinc-400 mr-auto">Unassign</Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="hover:bg-zinc-800">Cancel</Button>
          <Button data-testid="assign-customer-save" onClick={() => save(false)} disabled={saving || !customerId} className="bg-blue-600 hover:bg-blue-500">
            {saving ? "Saving…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
